import { useEffect, useState, type FormEvent } from 'react'
import { Globe2 } from 'lucide-react'
import Field from '../components/forms/Field'
import { useNavigate } from 'react-router-dom'
import { apiJson } from '../lib/api'
import PhotoUpload from '../components/PhotoUpload'
// Migration 0021 — the same control PlayerEdit uses for passports and
// languages. It is entity-agnostic (title/icon/max/rows/onChange), so it is
// imported rather than forked despite living under components/players/.
import StringListForm from '../components/players/StringListForm'
import { countryOptions } from '../lib/countries'
import { educationLevelOptions } from '../lib/education'
import { LANGUAGES } from '../lib/languages'
import {
  DIAL_CODES,
  splitWhatsapp,
  composeWhatsapp,
  whatsappNeedsCountryCode,
} from '../lib/phone'
import { stripSocial, composeSocial, SOCIAL_PREFIX } from '../lib/socials'

interface CoachDashboardData {
  coach: {
    id: string
    name: string
    current_team: string | null
    country: string | null
    role: string | null
    fiba_cert_number: string | null
    is_verified: boolean
    bio: string | null
    photo_url: string | null
    // Migration 0017 — public-profile fields.
    slug: string | null
    credential_headline: string | null
    education: string | null
    // Migration 0022 — degree ladder.
    education_level: string | null
    instagram_url: string | null
    youtube_url: string | null
    facebook_url: string | null
    // Migration 0018 — own contact (raw values, owner-scoped endpoint).
    contact_email: string | null
    whatsapp: string | null
    // Migration 0021 — multi-value languages.
    languages: string[]
  } | null
}

interface FormState {
  name: string
  current_team: string
  country: string
  role: string
  fiba_cert_number: string
  bio: string
  // Migration 0017 — public-profile fields. URLs are entered as full
  // https links; the server validates protocol and clears on empty.
  credential_headline: string
  education: string
  // Migration 0022 — '' means the placeholder is still selected, which the
  // server maps to NULL. Never pre-filled with a level.
  education_level: string
  // B5 — the three social fields also left FormState: they are held as
  // {value, isFullUrl} handle state and composed into URLs on save.
  // Migration 0018 — Pro-gated contact (values shown only to Coach Pro
  // viewers on the public surfaces). B4 — whatsapp left FormState too; it is
  // composed from waCode + waNumber.
  contact_email: string
}

function blank(): FormState {
  return {
    name: '',
    current_team: '',
    country: '',
    role: '',
    fiba_cert_number: '',
    bio: '',
    credential_headline: '',
    education: '',
    education_level: '',
    contact_email: '',
  }
}

export default function CoachEdit() {
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(blank())
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  // B4 — WhatsApp is entered as code + national number and recomposed into
  // the single existing coaches.whatsapp column on save. No migration.
  const [waCode, setWaCode] = useState('')
  const [waNumber, setWaNumber] = useState('')
  // B5 — socials are entered as handles. `isFullUrl` marks a stored value we
  // must not reduce (YouTube /channel/, Facebook profile.php), in which case
  // the prefix label is hidden and the whole URL is shown.
  const [instagram, setInstagram] = useState({ value: '', isFullUrl: false })
  const [youtube, setYoutube] = useState({ value: '', isFullUrl: false })
  const [facebook, setFacebook] = useState({ value: '', isFullUrl: false })
  // Migration 0021 — kept out of FormState (which is all plain strings),
  // alongside the other non-string fields.
  const [languages, setLanguages] = useState<string[]>([])
  // Migration 0017 — public-profile slug, for the "View public profile"
  // link. Read-only here (slug isn't editable via PUT /api/coach).
  const [slug, setSlug] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function bootstrap() {
      try {
        const data = await apiJson<CoachDashboardData>('/api/dashboard/coach')
        if (cancelled) return
        if (!data.coach) {
          navigate('/onboarding/coach', { replace: true })
          return
        }
        setForm({
          name: data.coach.name,
          current_team: data.coach.current_team ?? '',
          country: data.coach.country ?? '',
          role: data.coach.role ?? '',
          fiba_cert_number: data.coach.fiba_cert_number ?? '',
          bio: data.coach.bio ?? '',
          credential_headline: data.coach.credential_headline ?? '',
          education: data.coach.education ?? '',
          education_level: data.coach.education_level ?? '',
          contact_email: data.coach.contact_email ?? '',
        })
        setPhotoUrl(data.coach.photo_url)
        setLanguages(data.coach.languages ?? [])
        const wa = splitWhatsapp(data.coach.whatsapp)
        setWaCode(wa.code)
        setWaNumber(wa.number)
        setInstagram(stripSocial('instagram', data.coach.instagram_url))
        setYoutube(stripSocial('youtube', data.coach.youtube_url))
        setFacebook(stripSocial('facebook', data.coach.facebook_url))
        setSlug(data.coach.slug)
        setLoading(false)
      } catch (e) {
        if (cancelled) return
        const msg = e instanceof Error ? e.message : 'unknown_error'
        if (msg === 'role_mismatch' || msg === 'unauthorized') {
          navigate('/', { replace: true })
          return
        }
        setErr(msg)
        setLoading(false)
      }
    }
    bootstrap()
    return () => {
      cancelled = true
    }
  }, [navigate])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  // C1b — the country code is required once a number is typed. Caught here so
  // the coach sees it next to the field instead of a bare 400 after a submit.
  // The server enforces the same rule; this is the courtesy layer.
  const waMissingCode = whatsappNeedsCountryCode(waCode, waNumber)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    if (waMissingCode) {
      setErr('whatsapp_country_code_required')
      return
    }
    setSubmitting(true)
    setErr(null)
    try {
      await apiJson('/api/coach', {
        method: 'PUT',
        body: JSON.stringify({
          name: form.name.trim(),
          current_team: form.current_team.trim(),
          country: form.country.trim(),
          role: form.role.trim(),
          fiba_cert_number: form.fiba_cert_number.trim(),
          bio: form.bio.trim(),
          credential_headline: form.credential_headline.trim(),
          education: form.education.trim(),
          // B2 — empty string means the placeholder is still selected;
          // strOrNull maps it to NULL server-side.
          education_level: form.education_level.trim(),
          // B5 — composed back into absolute https URLs; storage unchanged.
          instagram_url: composeSocial('instagram', instagram.value),
          youtube_url: composeSocial('youtube', youtube.value),
          facebook_url: composeSocial('facebook', facebook.value),
          contact_email: form.contact_email.trim(),
          // B4 — recomposed into the single existing column.
          whatsapp: composeWhatsapp(waCode, waNumber),
          // Migration 0021 — empty rows dropped here, mirroring the films /
          // player-languages payload filter, so a stray "Add" click never
          // blocks a save.
          languages: languages.map((l) => l.trim()).filter(Boolean),
        }),
      })
      // Land on the public profile so a save visibly produces something.
      // Bouncing to /dashboard was the whole "I can't see what changes
      // I've made" complaint (Kaillum, 07-28).
      navigate(slug ? `/coaches/${slug}` : '/dashboard', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="text-hoop-text-3">Loading…</p>
      </main>
    )
  }

  return (
    <main className="max-w-xl mx-auto px-6 py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-hoop-text">
          Edit coach profile
        </h1>
        <p className="text-hoop-text-3 text-sm mt-1">
          Changes apply to references you've already written and any new ones.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-5">
        <PhotoUpload
          entityType="coach"
          currentPhotoUrl={photoUrl}
          onChange={setPhotoUrl}
        />

        <Field label="Your name" required>
          <input
            type="text"
            required
            value={form.name}
            onChange={e => update('name', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Current team / program">
          <input
            type="text"
            value={form.current_team}
            onChange={e => update('current_team', e.target.value)}
            className={inputClass}
          />
        </Field>

        {/* B1 — canonical list from src/lib/countries.ts, NOT
            /api/players/countries (that endpoint is derived from existing
            player rows, so a coach from a country with no players could not
            select their own). countryOptions prepends an unrecognised stored
            value so a saved country is never silently blanked. */}
        <Field label="Country">
          <select
            value={form.country}
            onChange={e => update('country', e.target.value)}
            className={inputClass}
          >
            <option value="">Select a country</option>
            {countryOptions(form.country).map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Role" hint="e.g. 'Head Coach', 'Assistant', 'Scout'">
          <input
            type="text"
            value={form.role}
            onChange={e => update('role', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="FIBA certification number">
          <input
            type="text"
            value={form.fiba_cert_number}
            onChange={e => update('fiba_cert_number', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Bio">
          <textarea
            rows={4}
            value={form.bio}
            onChange={e => update('bio', e.target.value)}
            className={inputClass}
          />
        </Field>

        {/* Migration 0017 — public-profile fields. Rendered on
            /coaches/:slug. */}
        <Field
          label="Credential headline"
          hint="One line under your name, e.g. 'FIBA-licensed · Head Coach · BC Prime · ex-U18 NT'"
        >
          <input
            type="text"
            value={form.credential_headline}
            onChange={e => update('credential_headline', e.target.value)}
            className={inputClass}
          />
        </Field>

        {/* Migration 0022 — two independent optional fields. The placeholder
            is a real, selectable state: leaving it writes NULL, never a
            guessed "Bachelor's Degree". Nothing is pre-selected and nothing
            was backfilled, so a coach who has not told us their education
            does not publish a claim by opening this form and saving. */}
        <Field
          label="Education level"
          hint="Optional. Leave unselected if you'd rather not say."
        >
          <select
            value={form.education_level}
            onChange={e => update('education_level', e.target.value)}
            className={inputClass}
          >
            <option value="">Select…</option>
            {educationLevelOptions(form.education_level).map(l => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="School / Institution"
          hint="Where you studied. Separate from the level above."
        >
          <input
            type="text"
            value={form.education}
            onChange={e => update('education', e.target.value)}
            className={inputClass}
          />
        </Field>

        {/* Migration 0021 — replace-all on save, cap server-enforced at 6.
            The UI disables "Add" at the cap so the 400 is unreachable from
            here. */}
        <StringListForm
          title="Languages spoken"
          description="Languages you can communicate in with players and staff."
          icon={Globe2}
          addLabel="Add language"
          placeholder="Select a language"
          max={6}
          rows={languages}
          onChange={setLanguages}
          // B3 — opt-in dropdown. PlayerEdit passes no `options` and is
          // unchanged.
          options={[...LANGUAGES]}
        />

        {/* B5 — handle-only inputs with a static platform prefix. Storage is
            unchanged: composeSocial rebuilds the absolute https URL on save,
            so validateHttpsUrl, SocialIcons and the public profile are
            untouched. A stored value we can't safely reduce (YouTube
            /channel/, Facebook profile.php) renders as a full URL with the
            prefix hidden. */}
        <SocialField
          label="Instagram"
          platform="instagram"
          state={instagram}
          onChange={setInstagram}
        />
        <SocialField
          label="YouTube"
          platform="youtube"
          state={youtube}
          onChange={setYoutube}
          hint="Your @handle, or paste a full channel URL."
        />
        <SocialField
          label="Facebook"
          platform="facebook"
          state={facebook}
          onChange={setFacebook}
          hint="Your page name, or paste a full profile URL."
        />

        {/* Migration 0018 — Pro-gated contact fields. */}
        <Field
          label="Contact email"
          hint="Shown to Coach Pro subscribers on your profile and your references."
        >
          <input
            type="email"
            value={form.contact_email}
            onChange={e => update('contact_email', e.target.value)}
            placeholder="you@club.com"
            className={inputClass}
          />
        </Field>

        {/* B4 — split input, single stored column. */}
        <Field
          label="WhatsApp"
          hint="Shown to Coach Pro subscribers on your profile and your references."
        >
          {/* A1 — the width goes on a WRAPPER, not on the select.
              `inputClass` starts with `w-full`, and Tailwind resolves
              conflicting width utilities by CSS source order rather than
              class-list order, so appending `w-[7.5rem]` lost to `w-full` and
              the select took the whole row. A native <select> is also a flex
              item with `min-width: auto`, so it additionally wanted to be as
              wide as its longest option ("+31 · Netherlands").
              Inside the wrapper the select is no longer a flex item, so
              `w-full min-w-0` pins it to the wrapper. No importance override
              was used, and `inputClass` itself is untouched — every other
              field on this form depends on its `w-full`. */}
          <div className="flex gap-2 min-w-0">
            <div className="w-[7.5rem] shrink-0">
              <select
                value={waCode}
                onChange={e => setWaCode(e.target.value)}
                aria-label="WhatsApp country code"
                className={inputClass + ' min-w-0'}
              >
                <option value="">Code</option>
                {DIAL_CODES.map(d => (
                  <option key={`${d.code}-${d.country}`} value={d.code}>
                    +{d.code} · {d.country}
                  </option>
                ))}
              </select>
            </div>
            <input
              type="tel"
              inputMode="numeric"
              value={waNumber}
              onChange={e => setWaNumber(e.target.value)}
              aria-label="WhatsApp number"
              placeholder="81 234 5678"
              className={inputClass + ' flex-1 min-w-0'}
            />
          </div>
          {/* C2b — a stored value we could not split confidently arrives here
              whole, with the code unselected. Kaillum must see WHY the save
              will be refused, not just hit a 400. The forcing function is
              intended; a silent trap is not. */}
          {waMissingCode && (
            <p className="mt-1.5 text-xs text-hoop-red">
              Select your country code to save. Your number is stored as
              country code + number so it can be dialled from abroad.
            </p>
          )}
        </Field>

        {slug && (
          <p className="text-xs text-hoop-text-3">
            Your public profile:{' '}
            <a
              href={`/coaches/${slug}`}
              className="text-hoop-accent underline hover:no-underline"
            >
              /coaches/{slug}
            </a>
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="bg-hoop-primary text-hoop-primary-fg px-6 py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="text-hoop-text-2 hover:text-hoop-text text-sm"
          >
            Cancel
          </button>
          {err && (
            <p className="text-sm text-hoop-red">{errorMessage(err)}</p>
          )}
        </div>
      </form>
    </main>
  )
}

const inputClass =
  'w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent'

// C1b — the form-level message. The server answers with error KEYS; showing
// `whatsapp_country_code_required` raw to a non-technical coach is not "a clear
// message". Unknown keys fall through unchanged rather than being swallowed.
function errorMessage(key: string): string {
  switch (key) {
    case 'whatsapp_country_code_required':
      return 'Select your WhatsApp country code — a number without one cannot be dialled from abroad.'
    case 'whatsapp_invalid':
      return 'That WhatsApp number does not look right. Use digits only, 7–15 of them after the country code.'
    case 'contact_email_invalid':
      return 'That email address does not look right.'
    case 'education_level_invalid':
      return 'Pick an education level from the list.'
    case 'too_many_languages':
      return 'Up to 6 languages.'
    default:
      return key
  }
}

// B5 — one social row: static platform prefix + handle input. When the stored
// value could not be safely reduced to a handle (YouTube /channel/ ids,
// Facebook profile.php?id=), `isFullUrl` is set and we render a plain URL
// input with no prefix, because prefixing it would produce a dead link.
function SocialField({
  label,
  platform,
  state,
  onChange,
  hint,
}: {
  label: string
  platform: 'instagram' | 'youtube' | 'facebook'
  state: { value: string; isFullUrl: boolean }
  onChange: (next: { value: string; isFullUrl: boolean }) => void
  hint?: string
}) {
  // Typing something URL-shaped into the handle box flips the row to
  // full-URL mode, so a pasted channel link is kept verbatim.
  function handleInput(value: string) {
    const looksUrl = /^https?:\/\//i.test(value) || /\//.test(value)
    onChange({ value, isFullUrl: looksUrl })
  }
  return (
    <Field label={label} hint={hint}>
      {state.isFullUrl ? (
        <input
          type="url"
          value={state.value}
          onChange={e => handleInput(e.target.value)}
          placeholder={`https://www.${platform}.com/…`}
          className={inputClass}
        />
      ) : (
        <div className="flex items-stretch">
          <span className="inline-flex items-center rounded-l-md border border-r-0 border-hoop-border bg-hoop-bg px-2.5 text-xs text-hoop-text-3 whitespace-nowrap">
            {SOCIAL_PREFIX[platform]}
          </span>
          <input
            type="text"
            autoComplete="off"
            value={state.value}
            onChange={e => handleInput(e.target.value)}
            placeholder={platform === 'youtube' ? '@handle' : 'yourhandle'}
            className={inputClass + ' rounded-l-none'}
          />
        </div>
      )}
    </Field>
  )
}

